import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { fetchWithAuth } from '@/lib/api';

export interface SignUpData {
  email: string;
  password: string;
  username: string;
}

export interface SignInData {
  email: string;
  password: string;
}

const googleProvider = new GoogleAuthProvider();

/**
 * Firebase ID 토큰을 localStorage에 저장
 */
async function saveToken(user: User) {
  const token = await user.getIdToken();
  localStorage.setItem('firebase_token', token);
  return token;
}

/**
 * 회원가입 — Firebase Auth 생성 후 백엔드 API로 프로필 생성
 */
export async function signUp({ email, password, username }: SignUpData) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    await updateProfile(userCredential.user, { displayName: username });
    await saveToken(userCredential.user);

    // 백엔드 API로 Firestore 프로필 생성 (Zod 검증 + 웰컴 가챠 지급)
    const res = await fetchWithAuth('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, agreedToTerms: true }),
    });

    const data = await res.json();
    if (!res.ok) {
      console.error('Register API error:', data);
    }

    const role = data.data?.role || 'user';

    return { user: userCredential.user, role, error: null };
  } catch (error: unknown) {
    console.error('Signup error:', error);
    const code = (error as { code?: string }).code || '';
    const message = getErrorMessage(code);
    return { user: null, role: 'user', error: message };
  }
}

/**
 * 이메일 로그인 — 백엔드 API로 lastLoginAt 업데이트
 */
export async function signIn({ email, password }: SignInData) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await saveToken(userCredential.user);

    // 백엔드 API로 로그인 처리
    const res = await fetchWithAuth('/api/auth/login', {
      method: 'POST',
    });

    const data = await res.json();
    const role = data.data?.role || 'user';

    return { user: userCredential.user, role, error: null };
  } catch (error: unknown) {
    console.error('Signin error:', error);
    const code = (error as { code?: string }).code || '';
    const message = getErrorMessage(code);
    return { user: null, role: 'user', error: message };
  }
}

/**
 * Google 로그인 — 신규 사용자는 백엔드 API로 프로필 생성
 */
export async function signInWithGoogle() {
  try {
    // popup 시도, COOP 이슈 시 redirect로 폴백
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider);
    } catch (popupError: unknown) {
      const code = (popupError as { code?: string }).code || '';
      if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') {
        // redirect 방식으로 폴백
        await signInWithRedirect(auth, googleProvider);
        return { user: null, role: 'user', error: null }; // redirect 후 페이지 리로드됨
      }
      throw popupError;
    }

    const user = result.user;
    await saveToken(user);

    // 먼저 기존 사용자인지 확인
    const meRes = await fetchWithAuth('/api/auth/me');

    let role: string;

    if (meRes.ok) {
      // 기존 사용자 — 로그인 처리
      const meData = await meRes.json();
      role = meData.data?.role || 'user';
      await fetchWithAuth('/api/auth/login', { method: 'POST' });
    } else {
      // 신규 사용자 — 백엔드 API로 프로필 생성
      const username = user.displayName || (user.email || '').split('@')[0] || 'User';
      const registerRes = await fetchWithAuth('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, agreedToTerms: true }),
      });

      const registerData = await registerRes.json();
      role = registerData.data?.role || 'user';
    }

    return { user, role, error: null };
  } catch (error: unknown) {
    console.error('Google signin error:', error);
    const code = (error as { code?: string }).code || '';
    if (code === 'auth/popup-closed-by-user') {
      return { user: null, role: 'user', error: null }; // 취소는 에러 아님
    }
    const message = getErrorMessage(code);
    return { user: null, role: 'user', error: message };
  }
}

/**
 * 로그아웃
 */
export async function logOut() {
  try {
    await signOut(auth);
    localStorage.removeItem('firebase_token');
    return { error: null };
  } catch (error: unknown) {
    console.error('Logout error:', error);
    return { error: (error as Error).message };
  }
}

/**
 * 사용자 정보 가져오기 — 백엔드 API 사용
 */
export async function getUserData(_uid: string) {
  try {
    const res = await fetchWithAuth('/api/auth/me');
    if (!res.ok) {
      return { data: null, error: 'User not found' };
    }
    const json = await res.json();
    return { data: json.data, error: null };
  } catch (error: unknown) {
    console.error('Get user error:', error);
    return { data: null, error: (error as Error).message };
  }
}

/**
 * 인증 상태 변경 감지
 */
export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // 토큰 자동 갱신
      await saveToken(user);
    } else {
      localStorage.removeItem('firebase_token');
    }
    callback(user);
  });
}

/**
 * Firebase 에러 코드 → 한글 메시지
 */
function getErrorMessage(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return '이미 사용 중인 이메일입니다.';
    case 'auth/invalid-email':
      return '유효하지 않은 이메일 형식입니다.';
    case 'auth/weak-password':
      return '비밀번호는 6자 이상이어야 합니다.';
    case 'auth/user-not-found':
      return '등록되지 않은 이메일입니다.';
    case 'auth/wrong-password':
      return '비밀번호가 올바르지 않습니다.';
    case 'auth/too-many-requests':
      return '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.';
    case 'auth/invalid-credential':
      return '이메일 또는 비밀번호가 올바르지 않습니다.';
    default:
      return '인증 중 오류가 발생했습니다.';
  }
}
